/** One voice note at a time, for the whole page.
 *
 *  A module, not a React context, because the rule has to run synchronously
 *  inside a tap handler. On iOS the first `play()` must happen in the gesture
 *  itself; anything that waits for a render or an await first is silently
 *  blocked by Safari, which is why Waveform mounts its <audio> with `autoPlay`
 *  rather than calling play() after a state update. Pausing the PREVIOUS
 *  element is synchronous and safe; touching the new one is not, so this never
 *  calls play().
 */
let held: HTMLMediaElement | null = null;

export const current = (): HTMLMediaElement | null => held;

/** Hand the claim to `el` and stop whoever had it. */
export function claim(el: HTMLMediaElement): void {
  // onPlay can fire more than once for a single tap -- a seek re-fires it --
  // and pausing yourself on the second one stops the note you just started.
  if (held === el) return;

  // The order is load-bearing. pause() fires the old Waveform's onPause, which
  // calls release() on that element; if `held` were still pointing at it, the
  // release would clear the claim the new note just took, and the note after
  // that would have nothing to stop.
  const previous = held;
  held = el;
  previous?.pause();
}

/** Give up the claim -- on pause, or on unmount. Ignored when someone else
 *  already holds it, because an old note's unmount arrives after the new one
 *  has started. */
export function release(el: HTMLMediaElement | null): void {
  if (el === null || held === el) held = null;
}
